import { useContext, useEffect, useState } from 'react';
import { Alert, Box, Button, Stack, SvgIcon, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Plus } from '../../../assets';
import { UserContext } from '../../../components/shared/useUser';
import { useNavigate, useLocation } from 'react-router-dom';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import BackButton from '../../../components/shared/actions/BackButton';
import SuppliersTab from './components/SuppliersTab';
import SupplierMasterList from './components/SupplierMasterList';
import { useDemoScenario } from '../../../contexts/DemoScenarioContext';
import { isDemoModeEnabled } from '../../../utils/featureAccess';
import { useInstanceMarket } from '../../../contexts/InstanceMarketContext';

const SuppliersPage = () => {
  const { t } = useTranslation('suppliers');
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const { hasPermissionTo } = useContext(UserContext);
  const { defaultInstanceId, defaultMarketId, defaultFiltersLoading } = useInstanceMarket();
  const [hasPermissionToCreateSupplier, setHasPermissionToCreateSupplier] = useState<boolean>(false);
  const { referrals } = useDemoScenario();
  const isDemoMode = isDemoModeEnabled();
  const pendingReferrals = referrals.filter((referral) => referral.status === 'PendingProcurementReview');

  const [currentFilterInstance, setCurrentFilterInstance] = useState<string>('');
  const [currentFilterMarket, setCurrentFilterMarket] = useState<string>('');
  const [currentFilterSearchTerm, setCurrentFilterSearchTerm] = useState<string>('');
  const [isInitializingFilters, setIsInitializingFilters] = useState<boolean>(false);

  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);

  useEffect(() => {
    setHasPermissionToCreateSupplier(hasPermissionTo(['SupplierCreate', 'ConfigurationAdmin']));

    const searchParams = new URLSearchParams(location.search);
    const companyIdParam = searchParams.get('companyId');
    const customerTypeIdParam = searchParams.get('customerTypeId');
    const searchTermParam = searchParams.get('searchTerm');

    setCurrentFilterInstance(companyIdParam || '');
    setCurrentFilterMarket(customerTypeIdParam || '');
    setCurrentFilterSearchTerm(searchTermParam || '');

  }, [hasPermissionTo, location.search]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (!searchParams.get('companyId') && !searchParams.get('customerTypeId') && !defaultFiltersLoading && defaultInstanceId && defaultMarketId) {
      setCurrentFilterInstance(defaultInstanceId);
      setCurrentFilterMarket(defaultMarketId);
      const params = new URLSearchParams();
      params.set('companyId', defaultInstanceId);
      params.set('customerTypeId', defaultMarketId);
      navigate(`?${params.toString()}`, { replace: true });
    }
  }, [defaultFiltersLoading, defaultInstanceId, defaultMarketId]);

  const handleCreateNew = () => {
    navigate('/procurement/suppliers/create');
  };

  const handleOnboardReferral = (referralId: string) => {
    navigate(`/procurement/suppliers/create?referralId=${referralId}`);
  };

  const handleFilterChange = (instance: string, market: string, searchTerm?: string) => {
    setCurrentFilterInstance(instance);
    setCurrentFilterMarket(market);
    const newSearchTerm = searchTerm !== undefined ? searchTerm : currentFilterSearchTerm;
    setCurrentFilterSearchTerm(newSearchTerm);

    const params = new URLSearchParams();
    if (instance) params.set('companyId', instance);
    if (market) params.set('customerTypeId', market);
    if (newSearchTerm) params.set('searchTerm', newSearchTerm);
    navigate(`?${params.toString()}`, { replace: true });
  };

  const canViewSuppliers = hasPermissionTo(['SupplierView', 'ConfigurationAdmin']);

  if (!canViewSuppliers) {
    return null;
  }

  return (
    <PageArea>
      <BackButton
        handleClick={() => navigate('/settings')}
        title={t('backToSettings', 'Back to Settings')}
        isLink={false}
        styles={{ marginBottom: '20px' }} />
      <Stack
        width='100%'
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{
          justifyContent: 'space-between',
          mb: 4, // Add margin bottom for spacing
        }}
      >
        <Box width={isMobile ? '100%' : 'auto'}>
          <PageTitle>{t('pageTitle')}</PageTitle>
        </Box>

        {/* Demo Mode swaps the whole action bar for the master list's own primary actions
            (Add supplier / Request supplier / Export) below - see SupplierMasterList. */}
        {!isDemoMode && hasPermissionToCreateSupplier && (
          <Box
            width={isMobile ? '100%' : 'auto'}
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
            }}
          >
            <Button
              variant="primary"
              size="lg"
              startIcon={<SvgIcon><Plus fill='white' /></SvgIcon>}
              onClick={handleCreateNew}
              sx={{
                mb: { xs: 4, md: 0 },
              }}
            >
              {t('createNew')}
            </Button>
          </Box>
        )}
      </Stack>

      {isDemoMode && pendingReferrals.length > 0 && (
        <Stack spacing={2}
          sx={{ mb: 4 }}>
          {pendingReferrals.map((referral) => (
            <Alert
              key={referral.id}
              severity="info"
              action={(
                <Button color="inherit"
                  size="md"
                  onClick={() => handleOnboardReferral(referral.id)}>
                  {t('onboardNow', 'Onboard now')}
                </Button>
              )}
            >
              {t('referredByChefList', 'Referred by chef from "{{product}}": {{supplierName}} - {{reason}}', {
                product: referral.requestedByProduct,
                supplierName: referral.supplierName,
                reason: referral.reason,
              })}
            </Alert>
          ))}
        </Stack>
      )}

      {!isInitializingFilters && (
        isDemoMode ? (
          <SupplierMasterList />
        ) : (
          <SuppliersTab 
            onCreateNew={handleCreateNew}
            onFilterChange={handleFilterChange}
            initialInstance={currentFilterInstance}
            initialMarket={currentFilterMarket}
            initialSearchTerm={currentFilterSearchTerm}
          />
        )
      )}
    </PageArea>
  );
};

export default SuppliersPage;
